import { useQuery } from "@apollo/client";
import { GET_ORDER_DETAILS } from "../../queries/GetOrderDetails";
import {
  GetOrderDetailsResponse,
  GetOrderDetailsVariables,
  ProcessedOrderDetails,
  OrderLine,
  OrderState,
} from "./OrderDetailsTypes";

export const useOrderDetailsPage = (
  orderCode: string
): ProcessedOrderDetails => {
  const { data, loading, error } = useQuery<
    GetOrderDetailsResponse,
    GetOrderDetailsVariables
  >(GET_ORDER_DETAILS, {
    variables: { code: orderCode },
  });

  // Base shape for loading / error / no data
  if (loading || error || !data?.orderByCode) {
    return {
      loading,
      error,
      isFullyCancelled: false,
      cancelledAt: null,
      deliveries: [],
      unavailableItems: [],
      summary: undefined,
      raw: data?.orderByCode ?? null,
    };
  }

  const order = data.orderByCode;
  const isFullyCancelled = order.state.toLowerCase() === OrderState.CANCELLED;

  // ---------------------------
  // Unavailable items (cancelled lines) – always computed
  // ---------------------------
  const unavailableItems: OrderLine[] = order.subOrders.flatMap((sub) =>
    sub.lines.filter((l) => l.cancelledQuantity > 0)
  );

  // ---------------------------
  // Deliveries
  // ---------------------------
  let deliveries;

  if (isFullyCancelled) {
    // Combine ALL lines from all subOrders into one list
    const allLines = order.subOrders.flatMap((sub) => sub.lines);

    deliveries = [
      {
        subOrderId: "cancelled-merged",
        index: 1,
        state: "cancelled",
        deliveryDate: null,
        timeWindow: null,
        items: allLines,
      },
    ];
  } else {
    // Normal logic for active / partially cancelled orders
    deliveries = order.subOrders
      .map((sub, idx) => {
        const activeLines = sub.lines.filter((l) => l.cancelledQuantity === 0);

        // Skip empty deliveries for active orders
        if (activeLines.length === 0) return null;

        return {
          subOrderId: sub.id,
          index: idx + 1,
          state: sub.state,
          deliveryDate: activeLines[0]?.deliveryDate || null,
          timeWindow: sub.deliveryTime ?? null,
          items: activeLines,
        };
      })
      .filter((d): d is NonNullable<typeof d> => d !== null);
  }

  // ---------------------------
  // Summary – same for all states
  // ---------------------------
  const summary = {
    subtotal: order.subTotalWithTax,
    vat: order.subTotalWithTax - order.subTotal,
    deliveryFee: order.shippingWithTax,
    total: order.totalWithTax,
  };

  // ---------------------------
  // Final unified shape
  // ---------------------------
  return {
    loading,
    error,
    isFullyCancelled,
    cancelledAt: isFullyCancelled ? order.updatedAt ?? null : null,
    deliveries,
    unavailableItems,
    summary,
    raw: order,
  };
};
